import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Sparkline from "./Sparkline";


const fmtPrice = (n) =>
  n == null
    ? "—"
    : n >= 1000
      ? n.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : n >= 1
        ? n.toLocaleString("en-US", { maximumFractionDigits: 2 })
        : n.toFixed(4);

const fmtCap = (n) =>
  n == null
    ? "—"
    : n >= 1e12
      ? `${(n / 1e12).toFixed(2)}T`
      : n >= 1e9
        ? `${(n / 1e9).toFixed(1)}B`
        : `${(n / 1e6).toFixed(0)}M`;

function Row({ coin, index }) {
  const prev = useRef(coin.price);
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (prev.current !== coin.price) {
      setFlash(coin.price > prev.current ? "up" : "down");
      prev.current = coin.price;
      const t = setTimeout(() => setFlash(null), 1000);
      return () => clearTimeout(t);
    }
  }, [coin.price]);

  const up = (coin.change_24h ?? 0) >= 0;
  const flashBg =
    flash === "up" ? "bg-[#00FF41]/10" : flash === "down" ? "bg-[#FF003C]/10" : "";

  return (
    <motion.a
      href="/markets?tab=crypto"
      data-testid={`coin-row-${coin.symbol}`}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`group grid grid-cols-[2.25rem_1fr_auto] items-center gap-4 border-t border-white/10 px-4 py-3 transition-colors duration-700 hover:bg-white/[0.03] sm:grid-cols-[3rem_1.3fr_1fr_0.8fr_0.8fr_auto] ${flashBg}`}
    >
      <span className="text-xs text-white/30">{String(coin.rank ?? index + 1).padStart(2, "0")}</span>
      <span className="flex items-center gap-2.5 overflow-hidden">
        <img
          src={coin.logo}
          alt={coin.name}
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="h-6 w-6 shrink-0 rounded-full border border-white/10 bg-white/95 object-contain p-0.5"
        />
        <span className="flex items-baseline gap-2 overflow-hidden">
          <span className="text-sm text-white transition-colors duration-300 group-hover:text-gold">
            {coin.symbol.toUpperCase()}
          </span>
          <span className="hidden truncate text-[10px] text-white/35 md:inline">{coin.name}</span>
        </span>
      </span>
      <span
        data-testid={`price-${coin.symbol}`}
        className={`font-tabular text-sm ${
          flash === "up"
            ? "text-[#00FF41]"
            : flash === "down"
              ? "text-[#FF003C]"
              : "text-white/85"
        }`}
      >
        ${fmtPrice(coin.price)}
      </span>
      <span
        className={`hidden font-tabular text-xs sm:inline ${
          up
            ? "text-[#00FF41] drop-shadow-[0_0_6px_rgba(0,255,65,0.35)]"
            : "text-[#FF003C] drop-shadow-[0_0_6px_rgba(255,0,60,0.35)]"
        }`}
      >
        {up ? "+" : ""}
        {(coin.change_24h ?? 0).toFixed(2)}%
      </span>
      <span className="hidden font-tabular text-xs text-white/40 sm:inline">
        ${fmtCap(coin.market_cap)}
      </span>
      <Sparkline data={coin.sparkline} positive={up} />
    </motion.a>
  );
}

export default function LiveBoard({ coins, updatedAt, loading }) {
  return (
    <section data-testid="live-board" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="mb-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-gold">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00FF41] animate-pulse-dot" />
            The Board · Live
          </p>
          <h2 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Top twenty-five,
            <span className="italic text-gold"> ticking.</span>
          </h2>
        </div>
        <div className="flex flex-col items-start gap-2 md:items-end">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40" data-testid="board-updated-at">
            {updatedAt
              ? `Updated ${new Date(updatedAt * 1000).toLocaleTimeString()}`
              : "Connecting to feed…"}
          </p>
          <a
            data-testid="board-full-markets-link"
            href="/markets?tab=crypto"
            className="group flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold/80 transition-colors duration-300 hover:text-gold"
          >
            Full board on Aureon
            <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="border border-white/10 bg-[#141414]/40 backdrop-blur-xl"
      >
        <div className="grid grid-cols-[2.25rem_1fr_auto] gap-4 px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-white/35 sm:grid-cols-[3rem_1.3fr_1fr_0.8fr_0.8fr_auto]">
          <span>Rank</span>
          <span>Asset</span>
          <span>Price</span>
          <span className="hidden sm:inline">24h</span>
          <span className="hidden sm:inline">Mkt cap</span>
          <span className="hidden w-28 sm:block">7d</span>
        </div>
        {loading && coins.length === 0 ? (
          <div className="border-t border-white/10 px-4 py-10 text-center text-xs uppercase tracking-[0.3em] text-white/30" data-testid="board-loading">
            Opening the feed…
          </div>
        ) : (
          coins.map((coin, i) => <Row key={coin.id} coin={coin} index={i} />)
        )}
      </motion.div>
    </section>
  );
}
